<script setup lang="ts">
import type { RouteLocationRaw } from "#vue-router"

const props = withDefaults(
  defineProps<{
    content: Mandatory["quizzes"][number]["content"]
    deadline?: string
    completedAt?: string
    referrer?: string
    placement?: string
    dark?: boolean
  }>(),
  {
    deadline: undefined,
    completedAt: undefined,
    referrer: "",
    placement: "",
    dark: false,
  },
)

const link: RouteLocationRaw = {
  name: "multiple-choice-id",
  params: { id: props.content.id },
  query: { referrer: props.referrer, placement: props.placement },
}

const percentageCompleted = computed(() => {
  if (props.content.questions_count == 0) {
    return 0
  }

  return Math.round(
    (props.content.answers_count / props.content.questions_count) * 100,
  )
})

defineEmits(["link-clicked"])
</script>

<template>
  <div class="@container">
    <div class="relative flex flex-col gap-4 @2xl:flex-row">
      <NuxtLink :to="link" @click="$emit('link-clicked')">
        <div
          class="relative flex aspect-video items-center justify-center overflow-hidden rounded-xl bg-linear-to-br from-violet-50 to-indigo-100 p-5 ring-1 @2xl:h-40"
          :class="dark ? 'ring-zinc-800' : 'ring-zinc-200'"
        >
          <div
            class="w-full max-w-52 rotate-[-2deg] rounded-xl bg-white p-3 shadow-lg ring-1 ring-violet-200"
            role="img"
            :aria-label="$t('courses.content.quiz.title')"
          >
            <div class="mb-3 flex items-center gap-2">
              <div
                class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700"
              >
                <SvgoPikaQuestion class="size-5" />
              </div>
              <div class="h-2 w-2/3 rounded-full bg-zinc-200" />
            </div>
            <div class="space-y-2">
              <div
                class="flex items-center gap-2 rounded-lg border border-zinc-200 p-2"
              >
                <div
                  class="size-3 shrink-0 rounded-full border border-zinc-300"
                />
                <div class="h-1.5 w-3/4 rounded-full bg-zinc-200" />
              </div>
              <div
                class="flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 p-2"
              >
                <div
                  class="flex size-3 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white"
                >
                  <SvgoPikaCheck class="size-2" />
                </div>
                <div class="h-1.5 w-4/5 rounded-full bg-violet-200" />
              </div>
              <div
                class="flex items-center gap-2 rounded-lg border border-zinc-200 p-2"
              >
                <div
                  class="size-3 shrink-0 rounded-full border border-zinc-300"
                />
                <div class="h-1.5 w-1/2 rounded-full bg-zinc-200" />
              </div>
            </div>
          </div>
        </div>
      </NuxtLink>
      <div
        class="relative flex grow flex-col justify-between gap-4 @2xl:items-start"
      >
        <NuxtLink :to="link" @click="$emit('link-clicked')">
          <TypographyHeading :class="dark ? 'text-white' : ''">
            {{ $t("courses.content.quiz.title") }}
          </TypographyHeading>
          <p class="text-sm text-zinc-400">
            {{ $t("mandatory.quiz_questions", content.questions_count) }}
          </p>
          <MandatoryDeadlineBadge
            v-if="deadline"
            :deadline
            class="text-zinc-400!"
          />
        </NuxtLink>
        <FormButton
          :to="link"
          :variant="dark ? 'dark' : 'secondary'"
          @click="$emit('link-clicked')"
        >
          {{
            content.answers_count > 0
              ? $t("courses.progress.button")
              : $t("mandatory.start_quiz")
          }}
        </FormButton>
        <Badge
          v-if="completedAt"
          class="absolute top-1 right-1 hidden flex-row items-center justify-start @sm:flex"
        >
          <template #icon>
            <SvgoPikaRefresh />
          </template>
          {{ $t("mandatory.retake") }}
        </Badge>
        <div
          v-else-if="percentageCompleted > 0"
          class="absolute top-1 right-1 hidden flex-row items-center justify-start @sm:flex"
        >
          <div class="rounded-full bg-white p-0.5 ring-2 ring-zinc-100">
            <ProgressRing
              :radius="14"
              :progress="percentageCompleted"
              :stroke="3"
            />
          </div>
          <p
            class="pl-2 text-sm font-medium"
            :class="dark ? 'text-zinc-200' : 'text-zinc-800'"
          >
            {{ `${percentageCompleted}%` }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
